import { ArrowLeft, CalendarDays, Database, FileSpreadsheet, FileText, MapPin, Printer, Table2, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { loadAuthorizedReportData, type WebReportData } from "../features/reports/report-web-data";
import { getReportAgeBrackets } from "../features/reports/report-age-names";
import {
  REPORT_DEFINITIONS,
  flattenReportColumns,
  generateReportData,
  getReportColumnDepth,
  getReportColumnLeafCount,
  type ReportColumn,
  type ReportScopeOptions,
  type ReportTableData,
} from "../features/desktop-add-new-data/modules/reports/report-calculations";
import { getReportCategory } from "../features/desktop-add-new-data/modules/reports/report-ui";

const ALL_BARANGAYS = "__ALL_BARANGAYS__";

export function ReportDetailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { reportId: rawReportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<WebReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const reportId = parseReportId(rawReportId);
  const canUseMunicipalityScope = user?.role === "MUNICIPAL_CITY_USER" || user?.role === "MUNICIPAL_CITY_ADMINISTRATOR";
  const municipality = canUseMunicipalityScope
    ? searchParams.get("municipality") ?? municipalityName(user?.locationLabel ?? "")
    : "";
  const requestedBarangay = canUseMunicipalityScope && searchParams.get("scope") === "municipality"
    ? ALL_BARANGAYS
    : searchParams.get("barangay")
      ?? (canUseMunicipalityScope ? data?.barangays[0]?.name ?? "" : barangayName(user?.locationLabel ?? ""));
  const selectedBarangay = !canUseMunicipalityScope && data?.barangays[0]?.name
    ? data.barangays[0].name
    : requestedBarangay;
  const scopeOptions: ReportScopeOptions = {
    municipality,
    municipalityWide: canUseMunicipalityScope && selectedBarangay === ALL_BARANGAYS,
    barangay: selectedBarangay === ALL_BARANGAYS ? undefined : selectedBarangay,
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadAuthorizedReportData()
      .then((result) => { if (active) { setData(result); setError(""); } })
      .catch(() => { if (active) setError("Unable to generate this report right now. Please check submitted data and try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (canUseMunicipalityScope || !data?.barangays[0]?.name) return;
    const authorizedBarangay = data.barangays[0].name;
    const currentBarangay = searchParams.get("barangay");
    const currentScope = searchParams.get("scope");
    if (currentScope === "barangay" && currentBarangay === authorizedBarangay && !searchParams.has("municipality")) return;
    const next = new URLSearchParams();
    next.set("scope", "barangay");
    next.set("barangay", authorizedBarangay);
    setSearchParams(next, { replace: true });
  }, [canUseMunicipalityScope, data, searchParams, setSearchParams]);

  useEffect(() => {
    if (!canUseMunicipalityScope || searchParams.get("scope") === "municipality" || searchParams.get("barangay") || !data?.barangays[0]?.name) return;
    const next = new URLSearchParams(searchParams);
    if (municipality) next.set("municipality", municipality);
    next.set("scope", "barangay");
    next.set("barangay", data.barangays[0].name);
    setSearchParams(next, { replace: true });
  }, [canUseMunicipalityScope, data, municipality, searchParams, setSearchParams]);

  const reportData = useMemo(() => {
    // The server already restricts report data to the signed-in user's approved
    // Municipality/City scope. Do not re-filter municipality-wide reports by
    // the municipality text stored inside legacy/synchronized questionnaire
    // payloads because those values may be blank, coded, or formatted
    // differently from the account's location label.
    const calculationScope: ReportScopeOptions = scopeOptions.municipalityWide
      ? { municipalityWide: true }
      : scopeOptions;
    const generated = generateReportData(
      reportId,
      calculationScope,
      data?.residents ?? [],
      data?.households ?? [],
    );
    return scopeOptions.municipalityWide && municipality
      ? { ...generated, scopeLabel: municipality }
      : generated;
  }, [data, municipality, reportId, scopeOptions.barangay, scopeOptions.municipality, scopeOptions.municipalityWide]);
  const ageBrackets = useMemo(() => getReportAgeBrackets(reportData), [reportData]);
  const canAccessReportNames = canUseMunicipalityScope;
  const canChooseBarangay = canUseMunicipalityScope && (data?.barangays.length ?? 0) > 1;

  function backToReports() {
    const params = new URLSearchParams();
    if (scopeOptions.municipality) params.set("municipality", scopeOptions.municipality);
    if (scopeOptions.municipalityWide) params.set("scope", "municipality");
    else {
      params.set("scope", "barangay");
      if (scopeOptions.barangay) params.set("barangay", scopeOptions.barangay);
    }
    navigate(`/reports?${params.toString()}`);
  }

  function changeBarangay(value: string) {
    const next = new URLSearchParams(searchParams);
    if (municipality) next.set("municipality", municipality);
    if (value === ALL_BARANGAYS) {
      next.set("scope", "municipality");
      next.delete("barangay");
    } else {
      next.set("scope", "barangay");
      next.set("barangay", value);
    }
    setSearchParams(next, { replace: true });
  }

  function openAgeNames(ageBracket: string) {
    const params = new URLSearchParams();
    if (scopeOptions.municipality) params.set("municipality", scopeOptions.municipality);
    if (scopeOptions.municipalityWide) params.set("scope", "municipality");
    else {
      params.set("scope", "barangay");
      if (scopeOptions.barangay) params.set("barangay", scopeOptions.barangay);
    }
    params.set("age", ageBracket);
    navigate(`/reports/${reportData.reportId}/names?${params.toString()}`);
  }

  function exportExcel() {
    try {
      const html = buildExcelHtml(reportData);
      const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel;charset=utf-8" });
      downloadBlob(blob, reportFilename(reportData, "xls"));
      setFeedback(`Excel file generated for Table ${reportData.reportId}.`);
    } catch {
      setFeedback("Unable to generate Excel report.");
    }
  }

  function printReport(asPdf = false) {
    setFeedback(asPdf ? "Use the print dialog's Save as PDF option to export this report as PDF." : "Print dialog opened for this report.");
    window.setTimeout(() => window.print(), 40);
  }

  return <section className="desktop-report-detail-page">
    <header className="desktop-report-detail-hero">
      <button type="button" className="desktop-report-back" onClick={backToReports}><ArrowLeft />Back to Reports</button>
      <div className="desktop-report-detail-title"><div className="desktop-report-hero-icon"><Table2 /></div><div><span>REPORT VIEWER</span><h1>Table {reportData.reportId}. {reportData.title}</h1><p>{scopeOptions.municipalityWide ? "Municipality-wide" : "Barangay"}: {reportData.scopeLabel}</p></div></div>
      <div className="desktop-report-detail-action-stack">
        <label className="desktop-report-detail-barangay-filter">Barangay
          <select
            value={selectedBarangay}
            disabled={!canChooseBarangay}
            onChange={(event) => changeBarangay(event.currentTarget.value)}
          >
            {canChooseBarangay ? <option value={ALL_BARANGAYS}>All Barangays</option> : null}
            {data?.barangays.map((barangay) => <option key={barangay.id} value={barangay.name}>{barangay.name}</option>)}
          </select>
        </label>
        <div className="desktop-report-detail-actions"><button type="button" className="desktop-report-pdf" onClick={() => printReport(true)}><FileText />PDF</button><button type="button" className="desktop-report-excel green" onClick={exportExcel}><FileSpreadsheet />Excel</button><button type="button" className="desktop-report-print" onClick={() => printReport(false)}><Printer />Print</button></div>
      </div>
    </header>

    {feedback ? <div className="desktop-report-feedback">{feedback}</div> : null}
    {error ? <div className="form-error" role="alert">{error}</div> : null}

    <section className="desktop-report-meta-grid">
      <MetaCard icon={<Table2 />} label="Report" value={`Table ${reportData.reportId}`} helper={getReportCategory(reportData.reportId)} />
      <MetaCard icon={<MapPin />} label="Scope" value={reportData.scopeLabel} helper={scopeOptions.municipalityWide ? "Municipality-wide" : "Barangay"} />
      <MetaCard icon={<Users />} label="Included Records" value={`${reportData.residentCount.toLocaleString()} residents`} helper={`${reportData.householdCount.toLocaleString()} households`} />
      <MetaCard icon={<CalendarDays />} label="Date Created" value={formatDate(reportData.generatedAt)} helper="Submitted records only" />
    </section>

    {!loading && !error && reportData.residentCount + reportData.householdCount === 0 ? <div className="desktop-report-empty"><strong>No data for this scope.</strong><br />No submitted records found for this scope yet. Submit Add New Data records to populate this report.</div> : null}

    <section className="desktop-report-sheet-wrap">
      <article className="desktop-report-sheet" id="rbim-report-sheet">
        <header className="desktop-report-sheet-heading"><div className="desktop-report-sheet-logo"><img src="/logos/RBIM.png" alt="RBIM logo" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} /></div><p>{reportData.scopeLabel}</p><h2>Table {reportData.reportId}. {reportData.title}</h2></header>
        <div className="desktop-report-sheet-meta"><Database />Submitted records only <span>|</span> {reportData.residentCount} residents <span>|</span> {reportData.householdCount} households</div>
        {flattenReportColumns(reportData.columns).length >= 8 ? <p className="desktop-report-scroll-hint">Scroll horizontally to view the full report table.</p> : null}
        {canAccessReportNames && ageBrackets.length ? <p className="desktop-report-scroll-hint">Click an age bracket in the table to view the names included in that report row.</p> : null}
        {loading ? <div className="desktop-report-loading">Loading report data…</div> : <ReportTable reportData={reportData} onAgeClick={canAccessReportNames ? openAgeNames : undefined} />}
        {reportData.notes?.length ? <div className="desktop-report-notes">{reportData.notes.map((note) => <p key={note}>{note}</p>)}</div> : null}
        <footer>Date Created: {formatDate(reportData.generatedAt)}</footer>
      </article>
    </section>
  </section>;
}

function ReportTable({ reportData, onAgeClick }: { reportData: ReportTableData; onAgeClick?: (ageBracket: string) => void }) {
  const flatColumns = flattenReportColumns(reportData.columns);
  const headerRows = buildHeaderRows(reportData.columns);
  const ageColumn = flatColumns.find((column) => column.label.trim().toLowerCase() === "age");
  const ageBrackets = new Set(getReportAgeBrackets(reportData));
  return <div className="desktop-report-table-scroll"><table className="desktop-report-table" style={{ minWidth: Math.max(920, 180 + Math.max(0, flatColumns.length - 1) * 105) }}>
    <thead>{headerRows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell) => <th key={cell.key} colSpan={cell.colSpan} rowSpan={cell.rowSpan}>{cell.label}</th>)}</tr>)}</thead>
    <tbody>{reportData.rows.map((row, rowIndex) => <tr key={rowIndex} className={row.isTotal ? "is-total" : ""}>{flatColumns.map((column, columnIndex) => {
      const value = row[column.key];
      const label = String(value ?? "").trim();
      const isClickableAge = Boolean(onAgeClick) && !row.isTotal && column.key === ageColumn?.key && ageBrackets.has(label);
      return <td key={column.key} className={columnIndex === 0 ? "sticky-label" : ""}>{isClickableAge ? <button type="button" className="desktop-report-age-link" onClick={() => onAgeClick?.(label)}>{label}</button> : formatCell(value, row)}</td>;
    })}</tr>)}</tbody>
  </table></div>;
}

type HeaderCell = { key: string; label: string; colSpan: number; rowSpan: number };
function buildHeaderRows(columns: ReportColumn[]) {
  const depth = getReportColumnDepth(columns);
  const rows: HeaderCell[][] = Array.from({ length: depth }, () => []);
  fillHeaderRows(columns, rows, 0, depth);
  return rows;
}
function fillHeaderRows(columns: ReportColumn[], rows: HeaderCell[][], level: number, depth: number) {
  columns.forEach((column) => {
    const hasChildren = Boolean(column.children?.length);
    rows[level].push({ key: `${column.key}-${level}`, label: column.label, colSpan: hasChildren ? getReportColumnLeafCount(column) : 1, rowSpan: hasChildren ? 1 : depth - level });
    if (column.children?.length) fillHeaderRows(column.children, rows, level + 1, depth);
  });
}

function MetaCard({ icon, label, value, helper }: { icon: React.ReactNode; label: string; value: string; helper: string }) {
  return <article className="desktop-report-meta-card"><div>{icon}</div><section><span>{label}</span><strong>{value}</strong><p>{helper}</p></section></article>;
}

function buildExcelHtml(reportData: ReportTableData) {
  const headerRows = buildHeaderRows(reportData.columns);
  const flatColumns = flattenReportColumns(reportData.columns);
  const headers = headerRows.map((row) => `<tr>${row.map((cell) => `<th colspan="${cell.colSpan}" rowspan="${cell.rowSpan}">${escapeHtml(cell.label)}</th>`).join("")}</tr>`).join("");
  const rows = reportData.rows.map((row) => `<tr>${flatColumns.map((column) => `<td>${escapeHtml(formatCell(row[column.key], row))}</td>`).join("")}</tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>table{border-collapse:collapse;font-family:Arial,sans-serif}th,td{border:1px solid #7f8fa4;padding:6px;text-align:center}th{background:#dbeafe;font-weight:bold}h2,p{text-align:center}</style></head><body><p>${escapeHtml(reportData.scopeLabel)}</p><h2>Table ${reportData.reportId}. ${escapeHtml(reportData.title)}</h2><table><thead>${headers}</thead><tbody>${rows}</tbody></table><p>Date Created: ${escapeHtml(formatDate(reportData.generatedAt))}</p></body></html>`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
function reportFilename(reportData: ReportTableData, extension: string) {
  return `RBIM_Table_${reportData.reportId}_${reportData.scopeLabel.replace(/[^a-z0-9]+/gi, "_")}.${extension}`;
}
function escapeHtml(value: unknown) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" })[character] ?? character);
}
function formatCell(value: unknown, row: ReportTableData["rows"][number]) {
  if (typeof value === "number") return value === 0 && !row.isTotal ? "-" : String(value);
  return String(value ?? "").trim() || "-";
}
function parseReportId(value: string | undefined) {
  const id = Number(value);
  return REPORT_DEFINITIONS.some((definition) => definition.id === id) ? id : 1;
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }).format(date);
}
function municipalityName(locationLabel: string) { return locationLabel.split(",")[0]?.trim() ?? ""; }
function barangayName(locationLabel: string) { return locationLabel.split(",")[0]?.trim() ?? ""; }
